import { useCallback, useEffect, useRef, useState } from "react";
import { audibleTracks, dbToGain } from "@/lib/mixDeskApi";

const TAKE = "take";

function trackChain(ctx, el, dryBus, wetBus) {
  const src = ctx.createMediaElementSource(el);
  const low = Object.assign(ctx.createBiquadFilter(), { type: "lowshelf" });
  low.frequency.value = 120;
  const mid = Object.assign(ctx.createBiquadFilter(), { type: "peaking" });
  mid.frequency.value = 1000;
  mid.Q.value = 0.9;
  const high = Object.assign(ctx.createBiquadFilter(), { type: "highshelf" });
  high.frequency.value = 8000;
  const gain = ctx.createGain();
  const pan = ctx.createStereoPanner();
  const send = ctx.createGain();
  src.connect(low).connect(mid).connect(high).connect(gain).connect(pan).connect(dryBus);
  pan.connect(send).connect(wetBus);
  return { low, mid, high, gain, pan, send };
}

/** Master bus: level → warmth / brightness shelves → stereo width (mid/side) → safety limiter → meter. */
function masterChain(ctx) {
  const input = ctx.createGain();
  const warm = Object.assign(ctx.createBiquadFilter(), { type: "lowshelf" });
  warm.frequency.value = 150;
  const bright = Object.assign(ctx.createBiquadFilter(), { type: "highshelf" });
  bright.frequency.value = 6000;
  const split = ctx.createChannelSplitter(2);
  const merge = ctx.createChannelMerger(2);
  const g = { ll: ctx.createGain(), lr: ctx.createGain(), rl: ctx.createGain(), rr: ctx.createGain() };
  input.connect(warm).connect(bright).connect(split);
  split.connect(g.ll, 0); split.connect(g.lr, 0); split.connect(g.rl, 1); split.connect(g.rr, 1);
  g.ll.connect(merge, 0, 0); g.rl.connect(merge, 0, 0); g.lr.connect(merge, 0, 1); g.rr.connect(merge, 0, 1);
  const limiter = ctx.createDynamicsCompressor();
  limiter.knee.value = 0; limiter.ratio.value = 20; limiter.attack.value = 0.003; limiter.release.value = 0.06;
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  merge.connect(limiter).connect(analyser).connect(ctx.destination);
  return { input, warm, bright, g, limiter, analyser };
}

function applyMaster(m, settings, on) {
  const ms = settings.master;
  const active = on && ms.preset !== "off";
  m.input.gain.value = dbToGain(settings.master_db);
  m.warm.gain.value = active ? ms.warmth : 0;
  m.bright.gain.value = active ? ms.brightness : 0;
  const w = active ? ms.width / 100 : 1;
  m.g.ll.gain.value = (1 + w) / 2; m.g.rr.gain.value = (1 + w) / 2;
  m.g.lr.gain.value = (1 - w) / 2; m.g.rl.gain.value = (1 - w) / 2;
  m.limiter.threshold.value = active ? ms.ceiling : -0.5;
}

function applyTracks(nodes, settings, names) {
  const live = new Set(audibleTracks(settings.tracks, names));
  Object.entries(nodes).forEach(([name, n]) => {
    const t = settings.tracks[name];
    if (!t) return;
    n.low.gain.value = t.low; n.mid.gain.value = t.mid; n.high.gain.value = t.high;
    n.gain.gain.value = live.has(name) ? dbToGain(t.db) : 0;
    n.pan.pan.value = t.pan;
    n.send.gain.value = t.reverb / 100;
  });
}

async function loadReverb(ctx, reverb, wetBus, out) {
  const res = await fetch(reverb.url);
  const decoded = await ctx.decodeAudioData(await res.arrayBuffer());
  if (ctx.state === "closed") return;
  const conv = ctx.createConvolver();
  conv.normalize = false;
  conv.buffer = decoded;
  const scale = ctx.createGain();
  scale.gain.value = reverb.scale;
  wetBus.connect(conv).connect(scale).connect(out);
}

/**
 * Browser preview of the Mix Desk: one <audio> per part (+ your take), the same EQ / pan / reverb / master tone the
 * server render uses. Loudness is matched exactly only in the render.
 */
export default function useMixPreview({ stems, take, settings, reverb, masterOn }) {
  const ctxRef = useRef(null);
  const els = useRef({});
  const nodes = useRef({});
  const master = useRef(null);
  const [state, setState] = useState("loading");
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const live = useRef({ settings, masterOn });
  live.current = { settings, masterOn };
  const names = [...stems.map((s) => s.name), ...(take ? [TAKE] : [])];
  const namesKey = names.join(",");
  const offset = () => (live.current.settings.tracks[TAKE]?.offset_ms || 0) / 1000;

  useEffect(() => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctxRef.current = ctx;
    const dryBus = ctx.createGain();
    const wetBus = ctx.createGain();
    const m = masterChain(ctx);
    master.current = m;
    dryBus.connect(m.input);
    const sources = [...stems.map((s) => [s.name, s.mp3_url]), ...(take ? [[TAKE, take.vocal_url]] : [])];
    sources.forEach(([name, url]) => {
      const a = new Audio();
      a.preload = "auto";
      a.src = url;
      els.current[name] = a;
      nodes.current[name] = trackChain(ctx, a, dryBus, wetBus);
    });
    applyTracks(nodes.current, live.current.settings, sources.map((x) => x[0]));
    applyMaster(m, live.current.settings, live.current.masterOn);
    loadReverb(ctx, reverb, wetBus, m.input).catch(() => {});
    const lead = els.current[stems[0].name];
    const onMeta = () => { if (Number.isFinite(lead.duration)) setDuration(lead.duration); setState("ready"); };
    const onEnd = () => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); };
    const onErr = () => setState("error");
    lead.addEventListener("loadedmetadata", onMeta);
    lead.addEventListener("ended", onEnd);
    lead.addEventListener("error", onErr);
    return () => {
      Object.values(els.current).forEach((a) => { a.pause(); a.removeAttribute("src"); a.load(); });
      els.current = {};
      nodes.current = {};
      master.current = null;
      setPlaying(false);
      ctx.close();
    };
  }, [namesKey, take?.vocal_url, reverb.url]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { applyTracks(nodes.current, settings, names); }, [settings, namesKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (master.current) applyMaster(master.current, settings, masterOn); }, [settings, masterOn]);

  const syncTake = useCallback((now, force) => {
    const a = els.current[TAKE];
    if (!a) return;
    const want = now - offset();
    if (want < 0) { if (!a.paused) a.pause(); a.currentTime = 0; return; }
    if (force || Math.abs(a.currentTime - want) > 0.08) a.currentTime = want;
    if (a.paused && playing) a.play().catch(() => {});
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playing) return undefined;
    const t = setInterval(() => {
      const parts = stems.map((s) => els.current[s.name]).filter(Boolean);
      const now = parts[0]?.currentTime || 0;
      setTime(now);
      parts.slice(1).forEach((a) => { if (Math.abs(a.currentTime - now) > 0.08) a.currentTime = now; });
      syncTake(now, false);
    }, 200);
    return () => clearInterval(t);
  }, [playing, stems, syncTake]);

  const play = useCallback(async () => {
    const parts = stems.map((s) => els.current[s.name]).filter(Boolean);
    const at = parts[0]?.currentTime || 0;
    ctxRef.current?.resume();
    parts.forEach((a) => { a.currentTime = at; });
    const t = els.current[TAKE];
    const tw = at - offset();
    if (t) t.currentTime = Math.max(0, tw);
    try {
      await Promise.all([...parts.map((a) => a.play()), ...(t && tw >= 0 ? [t.play()] : [])]);
      setPlaying(true);
    } catch { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); }
  }, [stems]); // eslint-disable-line react-hooks/exhaustive-deps
  const pause = useCallback(() => { Object.values(els.current).forEach((a) => a.pause()); setPlaying(false); }, []);
  const seek = useCallback((t) => {
    stems.forEach((s) => { if (els.current[s.name]) els.current[s.name].currentTime = t; });
    syncTake(t, true);
    setTime(t);
  }, [stems, syncTake]);
  const getAnalyser = useCallback(() => master.current?.analyser || null, []);
  return { state, playing, time, duration, play, pause, seek, getAnalyser };
}
